import React from 'react';
import { useNetworkElementsStore } from '../../store/networkElementsStore';
import { useNetworkUIStore } from '../../store/networkUIStore';
import { AnyNetworkElement } from '../../types/network';
import { elementDisplayName } from '../../utils/elementDisplayName';

/**
 * "Weitere Elemente an dieser Stelle" — offered whenever the map click that
 * selected the current element found other elements within pixel tolerance
 * (cables sharing a trench, stacked markers). Clicking a row switches the
 * selection and keeps the remaining pile navigable (the previously selected
 * element joins the list), so every element of an overlap is reachable
 * without touching the map geometry.
 */

const TYPE_LABELS: Record<string, string> = {
  trafostation: 'Trafostation',
  kabelverteilschrank: 'Kabelverteilschrank',
  hausanschluss: 'Hausanschluss',
  abgangsleitung: 'Kabel',
  muffe: 'Muffe',
};

const TYPE_BADGE_CLASSES: Record<string, string> = {
  trafostation: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  kabelverteilschrank: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
  hausanschluss: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  abgangsleitung: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  muffe: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
};

export const CoLocatedElementsSection: React.FC = () => {
  const coLocatedIds = useNetworkUIStore(s => s.coLocatedElementIds);
  const selectedElement = useNetworkUIStore(s => s.selectedElement);
  const setSelectedElementFromMap = useNetworkUIStore(s => s.setSelectedElementFromMap);
  const setHighlightedElement = useNetworkUIStore(s => s.setHighlightedElement);
  const getElement = useNetworkElementsStore(s => s.getElement);
  const elements = useNetworkElementsStore(s => s.elements);
  const coLocated = React.useMemo(() => {
    const byId = new Map(elements.map(el => [el.id, el]));
    return coLocatedIds
      .map(id => byId.get(id))
      .filter((el): el is AnyNetworkElement => el != null);
  }, [elements, coLocatedIds]);

  if (coLocated.length === 0 || !selectedElement) return null;

  const switchTo = (target: AnyNetworkElement) => {
    // The pile stays navigable: the element we leave takes the target's slot.
    const nextIds = coLocatedIds.map(id => (id === target.id ? selectedElement.id : id));
    setSelectedElementFromMap(target, nextIds);
    setHighlightedElement(target.id);
  };

  return (
    <div className="bg-white dark:bg-primary-800 rounded-md shadow-sm border border-gray-200 dark:border-primary-600 overflow-hidden">
      <div className="px-3 py-2 bg-gray-50 dark:bg-primary-700 border-b border-gray-200 dark:border-primary-600">
        <h3 className="text-xs font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <svg className="w-4 h-4 text-gray-700 dark:text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          Weitere Elemente an dieser Stelle ({coLocated.length})
        </h3>
        <p className="mt-0.5 text-[10px] text-gray-500 dark:text-primary-300">
          Kabel in derselben Trasse und überlappende Elemente am Klickpunkt.
        </p>
      </div>
      <ul className="divide-y divide-gray-100 dark:divide-primary-700">
        {coLocated.map(el => (
          <li key={el.id}>
            <button
              type="button"
              onClick={() => switchTo(el)}
              title={`${TYPE_LABELS[el.type] ?? el.type} auswählen (${el.id})`}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-50 dark:hover:bg-primary-700/60 transition-colors"
            >
              <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold ${TYPE_BADGE_CLASSES[el.type] ?? 'bg-gray-100 text-gray-600 dark:bg-primary-700 dark:text-primary-200'}`}>
                {TYPE_LABELS[el.type] ?? el.type}
              </span>
              <span className="min-w-0 truncate text-xs text-gray-800 dark:text-primary-100">
                {elementDisplayName(el, getElement)}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};
